/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState, useContext } from 'react';

import { getPersona, isUnsignedESignNotificationPersona } from 'blb-persona/index';
import { BlendBridge } from 'blb-types/blendBridge';
import { MeContext } from 'blb-contexts/Me';
import { SubjectContext } from 'blb-contexts/Subject';
import { getPendingTasks } from './Task/taskUtils';
import { getClosingAppointmentConfig } from './ClosingAppointmentSection/ClosingAppointmentCard';

export namespace Overview {
  export interface Props {
    documents: BlendBridge.API.Document[];
    initialize: () => Promise<any>;
    loan: BlendBridge.API.Loan;
    removeDoc: (id: string) => void;
  }
}

export const Overview = ({ documents, loan, initialize, removeDoc }: Overview.Props): React.ReactElement => {
  const [areInstructionsOpen, setInstructionOpen] = useState<boolean>(false);

  const subject = useContext(SubjectContext);
  const me = useContext(MeContext)!;
  const persona = getPersona(me.principalType);

  const dashboardSections = getPendingTasks(
    initialize,
    me.principalType,
    loan.openTasks,
    loan?.closing?.specialInstructions,
    loan?.featureFlags,
    undefined,
    loan?.closing?.closingType,
  );

  if (me.principalType === 'ops_desk_agent' || me.principalType === 'settlement_agent') {
    const closingAppointmentSectionConfig = getClosingAppointmentConfig(
      loan.closing,
      loan.featureFlags?.isMultipleSchedulingEnabledOnBridge,
    );
    if (closingAppointmentSectionConfig) {
      dashboardSections.push(closingAppointmentSectionConfig);
    }
  }

  if (isUnsignedESignNotificationPersona(persona) && persona.shouldShowUnsignedESignUIs(subject)) {
    const unSignedBanner = persona.getESignBannerConfig(loan.closing);
    if (unSignedBanner) {
      dashboardSections.unshift(unSignedBanner);
    }
  }

  return persona.renderDashboardOverview(
    dashboardSections,
    loan,
    documents,
    removeDoc,
    setInstructionOpen,
    areInstructionsOpen,
  );
};
